import { theme } from 'aimo-ui';
import { useNavigationStateListener } from 'hooks/useNavigationStateListener';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BuyScreen,
  CompletedScreen,
  MapScreen,
} from 'screens/longTermParking/index';
import PermitAndParkZoneScreen from 'screens/longTermParking/PermitAndParkZoneScreen';
import WaitingListCompletedScreen from 'screens/longTermParking/waitingList/WaitingListCompletedScreen';
import WaitingListInfoScreen from 'screens/longTermParking/waitingList/WaitingListInfoScreen';
import WaitingListJoinScreen from 'screens/longTermParking/waitingList/WaitingListJoinScreen';
import WaitingListPermitDetailsScreen from 'screens/longTermParking/waitingList/WaitingListPermitDetailsScreen';
import { confirmCreateSalesOrderData } from 'states/common';
import { useActiveElementStore } from 'stores/activeElementStore';
import { isWeb } from 'utils/deviceUtils';
import { createStackNavigator } from 'utils/navigationHelpers';
import { getModalOptions, transitionOptions } from 'utils/navigationUtils';
import { ModalStackHeaderCustomProps } from './ModalStackHeader';
import { StackHeaderComponentTitleSmall } from './navigationComponents';

const styles = {
  flex: {
    flex: 1,
    borderTopLeftRadius: 140,
  },
  cardStyle: {
    backgroundColor: theme.colors.white,
    paddingHorizontal: 20,
  },
  thankYouStyle: {
    backgroundColor: theme.colors.lightBg.green,
    padding: 20,
  },
};
const LongTermParkingStack = createStackNavigator();

const LongTermParkingNavigator: React.FC = () => {
  const { t } = useTranslation();

  const modalOptions70 = useMemo(
    () => getModalOptions('70%', false, undefined, undefined, 400),
    []
  );

  const modalOptions95 = useMemo(
    () => getModalOptions('95%', false, undefined, undefined, 400, false),
    []
  );

  // Selected once so the reference stays stable, otherwise
  // useNavigationStateListener re-subscribes on every render.
  const setActiveElement = useActiveElementStore(
    (state) => state.setActiveElement
  );

  useNavigationStateListener({ onParkingZoneChange: setActiveElement });

  const GrayHeader = useCallback(
    (props: ModalStackHeaderCustomProps) => (
      <StackHeaderComponentTitleSmall
        {...props}
        background={isWeb ? theme.colors.white : theme.colors.gray100}
      />
    ),
    []
  );

  return (
    <LongTermParkingStack.Navigator
      screenOptions={{
        ...transitionOptions({ closeDuration: 600 }),
        cardStyle: styles.cardStyle,
        cardOverlayEnabled: true,
        header: StackHeaderComponentTitleSmall,
      }}>
      <LongTermParkingStack.Screen
        name="mapScreen"
        component={MapScreen}
        options={{
          cardStyle: undefined,
          headerShown: false,
          headerBackButtonDisplayMode: 'minimal',
          title: t('longTermParking.search'),
        }}
      />
      <LongTermParkingStack.Screen
        name="buyScreen"
        component={BuyScreen}
        options={{
          title: t('longTermParking.buyScreenTitle'),
        }}
        listeners={({ route }) => ({
          focus: () => {
            const currentData = confirmCreateSalesOrderData();
            const existingPmcId = currentData?.selectedPmc?.pmcId;
            const incomingPmcId = (
              route.params as { pmcId?: string } | undefined
            )?.pmcId;
            const isFreshPmc =
              !!existingPmcId && existingPmcId !== incomingPmcId;
            if ((!incomingPmcId || isFreshPmc) && !currentData?.unlockCode) {
              confirmCreateSalesOrderData(undefined);
            }
          },
          beforeRemove: () => {
            const currentData = confirmCreateSalesOrderData();
            if (currentData?.unlockCode) {
              confirmCreateSalesOrderData({
                ...currentData,
                unlockCode: undefined,
              });
            }
          },
        })}
      />
      <LongTermParkingStack.Screen
        name="waitingListInfo"
        component={WaitingListInfoScreen}
        options={{
          title: t('longTermParking.waitingList.title'),
        }}
      />
      <LongTermParkingStack.Screen
        name="waitingListJoin"
        component={WaitingListJoinScreen}
        options={{
          title: t('longTermParking.waitingList.title'),
        }}
      />
      <LongTermParkingStack.Screen
        name="waitingListPermitDetails"
        component={WaitingListPermitDetailsScreen}
        options={{
          cardStyle: [{ padding: 0, backgroundColor: theme.colors.gray100 }],
          title: t('longTermParking.waitingList.title'),
          headerBackButtonDisplayMode: 'minimal',
          headerTransparent: false,
          headerStyle: { backgroundColor: theme.colors.gray100 },
          header: GrayHeader,
        }}
      />

      <LongTermParkingStack.Group
        screenOptions={{
          ...modalOptions70.screenOptions,
          cardStyle: [modalOptions70.cardStyle, { padding: 20 }],
        }}>
        <LongTermParkingStack.Screen
          name="permitZone"
          component={PermitAndParkZoneScreen}
          options={{
            headerShown: false,
            headerBackButtonDisplayMode: 'minimal',
            title: t('longTermParking.search'),
          }}
        />
      </LongTermParkingStack.Group>
      <LongTermParkingStack.Group
        screenOptions={{
          ...modalOptions95.screenOptions,
          cardStyle: [modalOptions95.cardStyle, styles.thankYouStyle],
        }}>
        <LongTermParkingStack.Screen
          name="completed"
          component={CompletedScreen}
          options={{
            headerShown: false,
            title: t('longTermParking.complete.thankYou'),
          }}
        />
        <LongTermParkingStack.Screen
          name="waitingListCompleted"
          component={WaitingListCompletedScreen}
          options={{
            headerShown: false,
          }}
        />
      </LongTermParkingStack.Group>
    </LongTermParkingStack.Navigator>
  );
};

export default LongTermParkingNavigator;
